import React, { useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Wrench, Mail, Lock, User, Phone } from 'lucide-react';
import { trackUserRegistration, trackUserLogin } from '../utils/analytics';

const AuthModal = ({ isOpen, onClose, mode, setMode }) => {
  const { login } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'customer',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const payload = mode === 'login'
        ? { email: formData.email, password: formData.password }
        : formData;

      const res = await axios.post(`${API}${endpoint}`, payload);
      login(res.data.token, res.data.user);
      
      // Track analytics event
      if (mode === 'login') {
        trackUserLogin(res.data.user.role);
      } else {
        trackUserRegistration(res.data.user.role);
      }
      
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created successfully!');
      onClose();
      setFormData({ email: '', password: '', name: '', role: 'customer', phone: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${mode === 'login' ? 'login' : 'register'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent data-testid="auth-modal" className="max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="bg-[#003B5C] p-3 rounded-full">
              <Wrench className="w-8 h-8 text-white" />
            </div>
          </div>
          <DialogTitle className="text-2xl font-bold text-center text-[#003B5C]">
            {mode === 'login' ? 'Welcome Back' : 'Join SkillYaad'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <Label htmlFor="name" className="text-[#0F172A] font-medium">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="name"
                    data-testid="register-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-12 rounded-lg border-gray-200 focus:border-[#003B5C] focus:ring-2 focus:ring-[#003B5C]/20"
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="role" className="text-[#0F172A] font-medium">I am a...</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <button
                    type="button"
                    data-testid="role-customer-btn"
                    onClick={() => setFormData({ ...formData, role: 'customer' })}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all duration-300 text-sm ${
                      formData.role === 'customer'
                        ? 'bg-[#003B5C] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Customer
                  </button>
                  <button
                    type="button"
                    data-testid="role-worker-btn"
                    onClick={() => setFormData({ ...formData, role: 'worker' })}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all duration-300 text-sm ${
                      formData.role === 'worker'
                        ? 'bg-[#003B5C] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Worker
                  </button>
                  <button
                    type="button"
                    data-testid="role-admin-btn"
                    onClick={() => setFormData({ ...formData, role: 'admin' })}
                    className={`py-3 px-4 rounded-lg font-semibold transition-all duration-300 text-sm ${
                      formData.role === 'admin'
                        ? 'bg-[#003B5C] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Admin
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#0F172A] font-medium">Phone Number (Optional)</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="phone"
                    data-testid="register-phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10 h-12 rounded-lg border-gray-200 focus:border-[#003B5C] focus:ring-2 focus:ring-[#003B5C]/20"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="email" className="text-[#0F172A] font-medium">Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="email"
                data-testid={mode === 'login' ? 'login-email-input' : 'register-email-input'}
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10 h-12 rounded-lg border-gray-200 focus:border-[#003B5C] focus:ring-2 focus:ring-[#003B5C]/20"
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="password" className="text-[#0F172A] font-medium">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                id="password"
                data-testid={mode === 'login' ? 'login-password-input' : 'register-password-input'}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 h-12 rounded-lg border-gray-200 focus:border-[#003B5C] focus:ring-2 focus:ring-[#003B5C]/20"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            data-testid={mode === 'login' ? 'login-submit-btn' : 'register-submit-btn'}
            disabled={loading}
            className="w-full bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full py-3 font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Create Account'}
          </Button>
        </form>

        <div className="text-center mt-4">
          <p className="text-[#475569]">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button
              data-testid={mode === 'login' ? 'switch-to-register-btn' : 'switch-to-login-btn'}
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-[#003B5C] font-semibold hover:underline"
            >
              {mode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;